import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import toast from 'react-hot-toast';
import { TrendingUp, Car, Users, Route, Clock, DollarSign, AlertTriangle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatDateOnly } from '../lib/utils';

const Dashboard = () => {
  const [stats, setStats] = useState({
    total_rotas: 0,
    total_km: 0,
    total_custo: 0,
    total_horas_extras: 0
  });
  const [rotas, setRotas] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [gpsSaude, setGpsSaude] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const formatDate = (date) => date.toISOString().split('T')[0];

      const [dashboardRes, alertasRes, gpsRes] = await Promise.all([
        api.get('/relatorios/dashboard', {
          params: {
            data_inicio: formatDate(startOfMonth),
            data_fim: formatDate(endOfMonth)
          }
        }),
        api.get('/relatorios/alertas').catch(() => ({ data: { alertas: [] } })),
        api.get('/tecnicos/gps-saude').catch(() => ({ data: { tecnicos: [] } })),
      ]);

      setStats(dashboardRes.data);
      setRotas(dashboardRes.data.rotas.slice(0, 5));
      setAlertas(alertasRes.data?.alertas || []);
      setGpsSaude(gpsRes.data?.tecnicos || []);
    } catch (error) {
      toast.error('Erro ao carregar dashboard');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      title: 'Total de Rotas', 
      value: stats.total_rotas, 
      icon: Route, 
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'from-blue-50 to-cyan-50'
    },
    { 
      title: 'Km Percorridos', 
      value: `${stats.total_km.toFixed(0)} km`, 
      icon: TrendingUp, 
      color: 'from-purple-500 to-pink-500',
      bgColor: 'from-purple-50 to-pink-50'
    },
    { 
      title: 'Custo Total', 
      value: `R$ ${stats.total_custo.toFixed(2)}`, 
      icon: DollarSign, 
      color: 'from-emerald-500 to-teal-500',
      bgColor: 'from-emerald-50 to-teal-50'
    },
    { 
      title: 'Horas Extras', 
      value: `${stats.total_horas_extras.toFixed(1)}h`, 
      icon: Clock, 
      color: 'from-orange-500 to-red-500',
      bgColor: 'from-orange-50 to-red-50'
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-600 mt-1">Visão geral do mês atual</p>
        </div>
        <Link
          to="/rotas/nova"
          className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg hover:scale-105 transition-all"
          data-testid="nova-rota-btn"
        >
          + Nova Rota
        </Link>
      </div>

      {gpsSaude.filter((t) => t.gps_stale).length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            GPS parado (sem ponto há mais de 15 min)
          </h2>
          <ul className="space-y-2">
            {gpsSaude.filter((t) => t.gps_stale).map((t) => (
              <li key={t.tecnico_id} className="flex items-center justify-between gap-4">
                <span className="text-sm text-slate-700">
                  {t.tecnico_nome || 'Técnico'} em serviço
                  {t.segundos_sem_atualizacao != null
                    ? ` · último ponto há ${Math.round(t.segundos_sem_atualizacao / 60)} min`
                    : ' · nenhum ponto hoje'}
                </span>
                <Link
                  to="/mapa-tecnicos"
                  className="text-sm font-medium text-red-700 hover:text-red-800 whitespace-nowrap"
                >
                  Ver mapa →
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Alertas */}
      {alertas.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Alertas de hoje
          </h2>
          <ul className="space-y-2">
            {alertas.slice(0, 10).map((a, i) => (
              <li key={i} className="flex items-center justify-between gap-4">
                <span className="text-sm text-slate-700">{a.mensagem}</span>
                {a.rota_id && (
                  <Link
                    to={`/rotas/${a.rota_id}`}
                    className="text-sm font-medium text-amber-700 hover:text-amber-800 whitespace-nowrap"
                  >
                    Ver rota →
                  </Link>
                )}
              </li>
            ))}
          </ul>
          {alertas.length > 10 && (
            <p className="text-sm text-slate-500 mt-2">e mais {alertas.length - 10} alerta(s)</p>
          )}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              className={`bg-gradient-to-br ${stat.bgColor} rounded-2xl p-6 shadow-sm hover:shadow-md transition-all`}
              data-testid={`stat-card-${index}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
              <h3 className="text-sm font-medium text-slate-600">{stat.title}</h3>
              <p className="text-2xl font-bold text-slate-900 mt-1">{stat.value}</p>
            </div>
          );
        })}
      </div>

      {/* Recent Rotas */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-900">Rotas Recentes</h2>
          <Link
            to="/rotas"
            className="text-emerald-600 hover:text-emerald-700 font-medium transition-colors"
            data-testid="ver-todas-rotas-btn"
          >
            Ver todas →
          </Link>
        </div>

        {rotas.length === 0 ? (
          <div className="text-center py-12">
            <Route className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 mb-4">Nenhuma rota registrada ainda</p>
            <Link
              to="/rotas/nova"
              className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium"
            >
              Criar primeira rota →
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Data</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Técnico</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Carro</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">KM</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Custo</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Visitas</th>
                </tr>
              </thead>
              <tbody>
                {rotas.map((rota, index) => (
                  <tr key={rota.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="py-3 px-4 text-sm text-slate-900">
                      {formatDateOnly(rota.data)}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-900">{rota.tecnico_nome}</td>
                    <td className="py-3 px-4 text-sm text-slate-600">{rota.carro_placa}</td>
                    <td className="py-3 px-4 text-sm text-slate-900 font-medium">
                      {rota.km_total.toFixed(0)} km
                    </td>
                    <td className="py-3 px-4 text-sm text-emerald-600 font-medium">
                      R$ {rota.custo_total.toFixed(2)}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600">
                      {rota.visitas?.length || 0} cliente(s)
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
